import _ from 'lodash';
//services
import { validateStep1 } from 'services/beneficiary.services';
//reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	setBeneficiary,
	getNewBeneficiary,
	getMessage,
	setActiveStep,
	getBeneficiaryDetails,
	setMessage,
} from 'state/reducers/beneficiary.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
//components
import { invalidFields } from 'components/FormCreator/types';
import { validateFields, setListOfInvalids } from 'components/FormCreator/validation';
import WarningDialog from 'components/WarningDialog';
//sections
import BeneficiaryDetailsForm from './BeneficiaryDetailsForm';

//constants
import { Beneficiary, beneficiarySteps, fieldsValidations, BeneficiaryStatus } from 'constants/beneficiary.constants';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { pagesEnum, userMessage } from 'constants/ui.constants';
import Footer from '../Footer';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormsStack } from 'theme/elements/Forms';
import { cancelDialogProps, getUi, ifPageChange } from 'state/reducers/ui.reducers';
import CancelDialog from 'components/CancelDialog';
import { translatedCancelPopup } from 'utils';

export default function Step1(props: { handleCancel: any, isCountryChanged: any, setIsCountryChanged:any }) {
	const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const beneficiaryDetails = useAppSelector(getBeneficiaryDetails);
	const userMessage: userMessage = useAppSelector(getMessage);
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const prefixFields = `page.beneficiary.create.step.1.`;
	const isLeavingPage = useAppSelector(ifPageChange);
	const ui = useAppSelector(getUi);
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const dialogProps = useAppSelector(cancelDialogProps);
	const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);
	const location = useLocation();
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];

	const isNewBeneficiary = useMemo(() => _.isEqual(lastPath, 'new'), [lastPath]);

	useEffect(() => {
		if (isLeavingPage && ui.checkBeforeNavigate) {
			setOpenCancelDialog(true);
		}
	}, [isLeavingPage]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		if (name === 'countryId' && beneficiary.countryId && beneficiary.countryId !== value) {
			props.setIsCountryChanged(true)
		}
		dispatch(setBeneficiary({ name, value }));
		if (_.has(fieldsValidations, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, fieldsValidations, name, value));
		}
	};

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	const isValidForm = useMemo(() => {
		let obj = {};
		_.keys(beneficiaryDetails).forEach((key) => {
			obj = { ...obj, [key]: _.get(beneficiaryDetails, key) };
		});
		return validateFields(fieldsValidations, obj);
	}, [beneficiaryDetails]);

	const handleSubmit = (agreeDuplicate = false) => {
		const invalidFields = isValidForm;
		if (!_.keys(invalidFields).length) {
			const trimmedBeneficiary = Object.fromEntries(
				Object.entries(beneficiary).map(([key, value]) => [
					key,
					typeof value === 'string' ? value.trim() : value,
				])
			) as Beneficiary;
			if (beneficiary.id) {
				agreeDuplicate = true;
			}
			dispatch(validateStep1(trimmedBeneficiary, isNewBeneficiary, agreeDuplicate));
		} else {
			setInvalidFields(invalidFields);
		}
	};
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});
	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
				{...(userMessage.text === 'servers.errors.codes.1100' && {
					handleContinue: () => {
						dispatch(setMessage({ exist: false }));
						handleSubmit(true);
					},
					cancelLabel: t(`${prefixFields}validate_button.text`),
					continueLabel: t(`${prefixFields}cancel_button.text`),
				})}
			/>
			<CancelDialog
				open={openCancelDialog}
				{...dialogPropsTranslated}
				handleCancel={() => {
					setOpenCancelDialog(false);
					navigate(`/${pagesEnum.BENEFICIARY}`);
				}}
				handleContinue={() => {
					setOpenCancelDialog(false);
				}}
			/>
			<FormsStack>
				<BeneficiaryDetailsForm invalidFields={invalidFields} handleChange={handleChange} />
				<Footer
					handleCancel={() => setOpenCancelDialog(true)}
					handleBack={() => {
						dispatch(setActiveStep(beneficiarySteps.BANK_ACCOUNT));
					}}
					ifBack={false}
					disableSubmit={_.keys(isValidForm).length > 0 ? true : false}
					handleSubmit={handleSubmit}
					labelSubmit={t(`${prefixFields}validate_button.text`)}
					labelCancel={t(`${prefixFields}cancel_button.text`)}
				/>
			</FormsStack>
		</Fragment>
	);
}
